/**
 * One-time move of a Supabase auth session from an old `storageKey` to a new one.
 *
 * WHY THIS EXISTS. Renaming a `storageKey` is normally a breaking change for every
 * signed-in user: `supabase-js` reads the session from `localStorage` under exactly
 * that key, so a rename makes every existing session invisible and silently logs
 * everyone out. That is why the rename was deferred through the whole brand
 * remediation (audit §3, decision 4) rather than done with the rest of the copy.
 *
 * It only has to be deferred if the rename is done naively. Copying the value
 * across before the client is constructed makes the rename invisible to users:
 * the client looks up the new key, finds the session that was written under the
 * old one, and carries on.
 *
 * The old key is deleted after a successful copy, so this runs once per browser
 * and then does nothing. Keep the call in place for at least one release cycle
 * longer than the longest session lifetime — a user who has not opened the app
 * since before the rename still has their session under the old key.
 */
export const migrateAuthStorageKey = (
  fromKey: string,
  toKey: string,
  storage:
    | Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
    | undefined = safeLocalStorage(),
): 'migrated' | 'already-current' | 'nothing-to-migrate' | 'unavailable' => {
  if (!storage) return 'unavailable';

  try {
    // A session already under the new key wins. Overwriting it with an older one
    // would downgrade a fresh login to a stale session.
    if (storage.getItem(toKey) !== null) {
      storage.removeItem(fromKey);
      return 'already-current';
    }

    const existing = storage.getItem(fromKey);
    if (existing === null) return 'nothing-to-migrate';

    storage.setItem(toKey, existing);
    // Only remove the original once the copy is definitely readable — a failed
    // write here would otherwise destroy the session instead of moving it.
    if (storage.getItem(toKey) === existing) {
      storage.removeItem(fromKey);
    }
    return 'migrated';
  } catch {
    // Private mode, disabled storage, quota. A failed migration must not stop the
    // app from booting: the user simply sees a login screen, which is the same
    // outcome as before this function existed.
    return 'unavailable';
  }
};

/**
 * `localStorage` access throws rather than returning undefined in some privacy
 * modes, and is absent entirely during SSR, so it is probed rather than assumed.
 */
const safeLocalStorage = ():
  | Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
  | undefined => {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage;
  } catch {
    return undefined;
  }
};
